/* eslint-disable no-restricted-syntax */
import { useQuery } from '@tanstack/react-query';

import APIGateway from '@/api';
import {
  AgentConfig,
  AgentLowcode,
  AgentTypeEnum,
  BaseAgent
} from '@/types/agent';


export const agentsKeys = {
  all: [ 'agents' ] as const,
  list: () => [ ...agentsKeys.all, 'list' ] as const,
  detail: ( agentId: string ) => [ ...agentsKeys.all, 'detail', agentId ] as const,
  config: ( agentId: string, version: number ) => [ ...agentsKeys.all, 'config', agentId, version ] as const
};

export function useAgentsQuery( opts: { enabled: boolean } ) {
  return useQuery( {
    queryKey: agentsKeys.list(),
    queryFn: async() => {
      const { agents } = await APIGateway.agents.agentsGet();
      return agents;
    },
    enabled: opts.enabled
  } );
}

export function useAgentDetailQuery( agentId: string | undefined ) {
  return useQuery( {
    queryKey: agentsKeys.detail( agentId! ),
    queryFn: async() => {
      if ( !agentId ) throw new Error( 'Agent ID required' );

      const { agents } = await APIGateway.agents.agentsGet();
      const agent = agents.find( a => {
        if ( a.type === AgentTypeEnum.LOW_CODE ) {
          return ( a as BaseAgent<AgentLowcode> ).data.agentId === agentId;
        }
        return a.id === agentId;
      } );

      if ( !agent ) {
        throw new Error( 'Agent not found' );
      }

      return agent;
    },
    enabled: !!agentId
  } );
}

export function useAgentConfigQuery(
  agentId: string | undefined,
  version: number | undefined,
  enabled: boolean = true
) {
  return useQuery( {
    queryKey: agentsKeys.config( agentId!, version! ),
    queryFn: async(): Promise<AgentConfig> => {
      if ( !agentId ) throw new Error( 'Agent ID required' );
      if ( version === undefined ) throw new Error( 'Version required' );

      const config = await APIGateway.agents.agentConfigGet(
        { agentId, version }
      );
      return config;
    },
    enabled: !!agentId && version !== undefined && enabled,
    refetchOnMount: 'always'
  } );
}
